import { clone, delay } from "./client";
import { api, USE_MOCK_API } from "./http";
import { INITIAL_CART_ITEMS, PRODUCTS, PROMO_CODES } from "./mock-data";
import { validatePromo } from "./products";
import type { Cart, CartItem } from "./types";

const TAX_RATE = 0.06;

let items: CartItem[] = clone(INITIAL_CART_ITEMS);
let promoCode: string | null = null;

function computeCart(): Cart {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const rate = promoCode ? (PROMO_CODES[promoCode] ?? 0) : 0;
  const discount = Math.round(subtotal * rate * 100) / 100;
  const tax = Math.round((subtotal - discount) * TAX_RATE * 100) / 100;
  const shipping = 0;
  return {
    items: clone(items),
    subtotal,
    shipping,
    tax,
    discount,
    total: Math.round((subtotal - discount + tax + shipping) * 100) / 100,
    promoCode,
  };
}

/** Server carts carry no promo code — keep the field for the UI contract. */
function normalize(cart: Cart): Cart {
  return { ...cart, promoCode: cart.promoCode ?? null, items: cart.items ?? [] };
}

/** GET /api/cart */
export async function getCart(): Promise<Cart> {
  if (!USE_MOCK_API) return normalize(await api.get<Cart>("/cart"));
  return delay(computeCart(), 200);
}

/** POST /api/cart/items */
export async function addToCart(input: {
  productId: string;
  color: string;
  size: string;
  quantity?: number;
}): Promise<Cart> {
  if (!USE_MOCK_API) {
    return normalize(
      await api.post<Cart>("/cart/items", { ...input, quantity: input.quantity ?? 1 }),
    );
  }

  const product = PRODUCTS.find((p) => p.id === input.productId);
  if (!product) throw new Error("Product not found");

  const quantity = input.quantity ?? 1;
  const existing = items.find(
    (i) => i.productId === product.id && i.color === input.color && i.size === input.size,
  );

  if (existing) {
    existing.quantity += quantity;
  } else {
    items.push({
      id: `ci-${Date.now()}`,
      productId: product.id,
      slug: product.slug,
      name: product.name,
      ref: product.ref,
      image: product.images[0],
      color: input.color,
      size: input.size,
      price: product.price,
      quantity,
    });
  }

  return delay(computeCart());
}

/** PATCH /api/cart/items/:id — quantity 0 removes the line. */
export async function updateCartItem(id: string, quantity: number): Promise<Cart> {
  if (!USE_MOCK_API) {
    if (quantity <= 0) return removeFromCart(id);
    return normalize(
      await api.patch<Cart>(`/cart/items/${encodeURIComponent(id)}`, { quantity }),
    );
  }

  if (quantity <= 0) return removeFromCart(id);
  items = items.map((item) => (item.id === id ? { ...item, quantity } : item));
  return delay(computeCart(), 160);
}

/** DELETE /api/cart/items/:id */
export async function removeFromCart(id: string): Promise<Cart> {
  if (!USE_MOCK_API) {
    return normalize(await api.del<Cart>(`/cart/items/${encodeURIComponent(id)}`));
  }
  items = items.filter((item) => item.id !== id);
  return delay(computeCart(), 160);
}

/** DELETE /api/cart */
export async function clearCart(): Promise<Cart> {
  if (!USE_MOCK_API) {
    return normalize(await api.del<Cart>("/cart"));
  }
  items = [];
  return delay(computeCart(), 160);
}

/**
 * Promo codes are validated read-only (`POST /api/catalog/promo/validate`); the
 * discount is applied to an order by an admin, so the cart is returned as-is
 * with the code attached for display.
 */
export async function applyPromo(code: string): Promise<Cart> {
  const result = await validatePromo(code);
  if (!result.valid) throw new Error("That promo code isn't valid.");

  if (!USE_MOCK_API) {
    const cart = await getCart();
    return { ...cart, promoCode: result.code };
  }

  promoCode = result.code;
  return delay(computeCart(), 240);
}
